import React, { useEffect, useState } from "react";
import { admissionCellApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./admission-cell.scss";

const defaultData = {
  hero: { pageTitle: "Admission Cell", heading: "Welcome to Admissions", subheading: "Join our institution and build a bright future with quality education.", bannerImage: "", },
  about: { title: "About Admission Cell", description: "The Admission Cell manages all undergraduate and postgraduate admission processes transparently and efficiently.", },
  admissionProcess: [
    { id: 1, stepNumber: "01", title: "Register Online", description: "Fill the online application form on the official portal." },
    { id: 2, stepNumber: "02", title: "Submit Documents", description: "Upload all required documents for verification." },
  ],
  coursesOffered: [
    { id: 1, courseName: "B.Sc Computer Science", duration: "3 Years", level: "Undergraduate" },
    { id: 2, courseName: "M.Sc Mathematics", duration: "2 Years", level: "Postgraduate" },
  ],
  eligibility: { title: "Eligibility Criteria", description: "Candidates must have passed 10+2 with at least 50% marks from a recognized board.", },
  importantDates: [
    { id: 1, eventTitle: "Application Start Date", date: "2025-05-01", description: "Online applications begin." },
    { id: 2, eventTitle: "Last Date to Apply", date: "2025-06-30", description: "Deadline for application submission." },
  ],
  requiredDocuments: [
    { id: 1, documentName: "10th Marksheet", note: "Attested copy required." },
    { id: 2, documentName: "12th Marksheet", note: "Original + attested copy required." },
  ],
  contactInfo: { officeName: "Admission Cell Office", phone: "+91 98765 43210", email: "admissions@college.edu", address: "Ground Floor, Administrative Block, College Campus", officeHours: "Monday to Saturday, 10:00 AM - 4:00 PM", },
  applyNow: { buttonText: "Apply Now", buttonUrl: "https://admissions.college.edu/apply", },
  faqs: [
    { id: 1, question: "When does the admission process start?", answer: "Admissions typically begin in May every year." },
    { id: 2, question: "Is there an entrance exam?", answer: "Some programmes require an entrance test. Please check individual course details." },
  ],
  publish: { status: "draft", },
};

const sectionMeta = [
  { key: "hero", label: "Section 1: Hero", icon: "🖼️" },
  { key: "about", label: "Section 2: About", icon: "📄" },
  { key: "admissionProcess", label: "Section 3: Admission Process", icon: "🪜" },
  { key: "coursesOffered", label: "Section 4: Courses Offered", icon: "📚" },
  { key: "eligibility", label: "Section 5: Eligibility", icon: "✅" },
  { key: "importantDates", label: "Section 6: Important Dates", icon: "📅" },
  { key: "requiredDocuments", label: "Section 7: Required Documents", icon: "📁" },
  { key: "contactInfo", label: "Section 8: Contact Info", icon: "📞" },
  { key: "applyNow", label: "Section 9: Apply Now", icon: "🔗" },
  { key: "faqs", label: "Section 10: FAQs", icon: "❓" },
];

const AdmissionCell = () => {
  const [formData, setFormData] = useState(defaultData);
  const [openSections, setOpenSections] = useState({ hero: true });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await apiClient(admissionCellApi);
      const result = await response.json();
      if (response.ok && result.data) {
        setFormData(result.data);
      }
    } catch (error) {
      console.error("Error fetching admission cell content:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => { setOpenSections((prev) => ({ ...prev, [key]: !prev[key] })); };

  const updateSimple = (section, field, value) => {
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value }, }));
  };

  const updateListItem = (section, id, field, value) => {
    setFormData((prev) => ({ ...prev, [section]: prev[section].map((item) => item.id === id ? { ...item, [field]: value } : item), }));
  };

  const addListItem = (section, template) => {
    const newItem = { ...template, id: Date.now() };
    setFormData((prev) => ({ ...prev, [section]: [...prev[section], newItem], }));
  };

  const removeListItem = (section, id) => {
    setFormData((prev) => ({ ...prev, [section]: prev[section].filter((item) => item.id !== id), }));
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      const response = await apiClient(admissionCellApi, {
        method: "PUT",
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error("Error saving admission cell content:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ac-page">
      <div className="ac-page__header">
        <div><h1>Admission Cell</h1><p>Manage and configure all sections of the Admission Cell public page. Changes will reflect on the live website upon publishing.</p></div>
        <div className="ac-header-actions">
          <span className={`publish-badge ${formData.publish?.status || 'draft'}`}>● {formData.publish?.status === "published" ? "Published" : "Draft"}</span>
          <button className="save-btn" type="button" onClick={handleSave}>{loading ? "Saving..." : saved ? "✓ Saved" : "Save Changes"}</button>
        </div>
      </div>

      <form className="ac-form" onSubmit={handleSave}>
        {sectionMeta.map(({ key, label, icon }) => (
          <div className="ac-section-card" key={key}>
            <button type="button" className={`ac-section-header ${openSections[key] ? "open" : ""}`} onClick={() => toggleSection(key)}>
              <span className="section-icon">{icon}</span><span className="section-label">{label}</span>
              <span className={`chevron ${openSections[key] ? "up" : "down"}`}><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg></span>
            </button>

            {openSections[key] && (
              <div className="ac-section-body">
                {key === "hero" && (
                  <div className="section-fields">
                    <div className="form-row">
                      <div className="form-group"><label>Page Title</label><input type="text" value={formData.hero.pageTitle} onChange={(e) => updateSimple("hero", "pageTitle", e.target.value)} /></div>
                      <div className="form-group"><label>Heading</label><input type="text" value={formData.hero.heading} onChange={(e) => updateSimple("hero", "heading", e.target.value)} /></div>
                    </div>
                    <div className="form-group"><label>Subheading</label><input type="text" value={formData.hero.subheading} onChange={(e) => updateSimple("hero", "subheading", e.target.value)} /></div>
                    <div className="form-group"><label>Banner Image URL</label><input type="text" value={formData.hero.bannerImage} onChange={(e) => updateSimple("hero", "bannerImage", e.target.value)} /></div>
                  </div>
                )}
                {key === "about" && (
                  <div className="section-fields">
                    <div className="form-group"><label>About Title</label><input type="text" value={formData.about.title} onChange={(e) => updateSimple("about", "title", e.target.value)} /></div>
                    <div className="form-group"><label>About Description</label><textarea rows="5" value={formData.about.description} onChange={(e) => updateSimple("about", "description", e.target.value)} /></div>
                  </div>
                )}
                {key === "admissionProcess" && (
                  <div className="section-fields">
                    <div className="list-items">
                      {formData.admissionProcess.map((step, index) => (
                        <div className="list-item-card" key={step.id}>
                          <div className="list-item-header"><span className="item-index">Step {index + 1}</span><button type="button" className="remove-item-btn" onClick={() => removeListItem("admissionProcess", step.id)}>Remove</button></div>
                          <div className="form-row">
                            <div className="form-group"><label>Step Number</label><input type="text" value={step.stepNumber} onChange={(e) => updateListItem("admissionProcess", step.id, "stepNumber", e.target.value)} /></div>
                            <div className="form-group"><label>Step Title</label><input type="text" value={step.title} onChange={(e) => updateListItem("admissionProcess", step.id, "title", e.target.value)} /></div>
                          </div>
                          <div className="form-group"><label>Description</label><textarea rows="3" value={step.description} onChange={(e) => updateListItem("admissionProcess", step.id, "description", e.target.value)} /></div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="add-item-btn" onClick={() => addListItem("admissionProcess", { stepNumber: "", title: "", description: "" })}><span>+</span> Add Step</button>
                  </div>
                )}
                {key === "coursesOffered" && (
                  <div className="section-fields">
                    <div className="list-items">
                      {formData.coursesOffered.map((course, index) => (
                        <div className="list-item-card" key={course.id}>
                          <div className="list-item-header"><span className="item-index">Course {index + 1}</span><button type="button" className="remove-item-btn" onClick={() => removeListItem("coursesOffered", course.id)}>Remove</button></div>
                          <div className="form-row three-col">
                            <div className="form-group"><label>Course Name</label><input type="text" value={course.courseName} onChange={(e) => updateListItem("coursesOffered", course.id, "courseName", e.target.value)} /></div>
                            <div className="form-group"><label>Duration</label><input type="text" value={course.duration} onChange={(e) => updateListItem("coursesOffered", course.id, "duration", e.target.value)} /></div>
                            <div className="form-group"><label>Level</label><input type="text" value={course.level} onChange={(e) => updateListItem("coursesOffered", course.id, "level", e.target.value)} /></div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="add-item-btn" onClick={() => addListItem("coursesOffered", { courseName: "", duration: "", level: "" })}><span>+</span> Add Course</button>
                  </div>
                )}
                {key === "eligibility" && (
                  <div className="section-fields">
                    <div className="form-group"><label>Title</label><input type="text" value={formData.eligibility.title} onChange={(e) => updateSimple("eligibility", "title", e.target.value)} /></div>
                    <div className="form-group"><label>Description</label><textarea rows="5" value={formData.eligibility.description} onChange={(e) => updateSimple("eligibility", "description", e.target.value)} /></div>
                  </div>
                )}
                {key === "importantDates" && (
                  <div className="section-fields">
                    <div className="list-items">
                      {formData.importantDates.map((dateItem, index) => (
                        <div className="list-item-card" key={dateItem.id}>
                          <div className="list-item-header"><span className="item-index">Event {index + 1}</span><button type="button" className="remove-item-btn" onClick={() => removeListItem("importantDates", dateItem.id)}>Remove</button></div>
                          <div className="form-row">
                            <div className="form-group"><label>Event Title</label><input type="text" value={dateItem.eventTitle} onChange={(e) => updateListItem("importantDates", dateItem.id, "eventTitle", e.target.value)} /></div>
                            <div className="form-group"><label>Date</label><input type="date" value={dateItem.date} onChange={(e) => updateListItem("importantDates", dateItem.id, "date", e.target.value)} /></div>
                          </div>
                          <div className="form-group"><label>Description</label><input type="text" value={dateItem.description} onChange={(e) => updateListItem("importantDates", dateItem.id, "description", e.target.value)} /></div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="add-item-btn" onClick={() => addListItem("importantDates", { eventTitle: "", date: "", description: "" })}><span>+</span> Add Date</button>
                  </div>
                )}
                {key === "requiredDocuments" && (
                  <div className="section-fields">
                    <div className="list-items">
                      {formData.requiredDocuments.map((doc, index) => (
                        <div className="list-item-card" key={doc.id}>
                          <div className="list-item-header"><span className="item-index">Document {index + 1}</span><button type="button" className="remove-item-btn" onClick={() => removeListItem("requiredDocuments", doc.id)}>Remove</button></div>
                          <div className="form-row">
                            <div className="form-group"><label>Document Name</label><input type="text" value={doc.documentName} onChange={(e) => updateListItem("requiredDocuments", doc.id, "documentName", e.target.value)} /></div>
                            <div className="form-group"><label>Note</label><input type="text" value={doc.note} onChange={(e) => updateListItem("requiredDocuments", doc.id, "note", e.target.value)} /></div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="add-item-btn" onClick={() => addListItem("requiredDocuments", { documentName: "", note: "" })}><span>+</span> Add Document</button>
                  </div>
                )}
                {key === "contactInfo" && (
                  <div className="section-fields">
                    <div className="form-row">
                      <div className="form-group"><label>Office Name</label><input type="text" value={formData.contactInfo.officeName} onChange={(e) => updateSimple("contactInfo", "officeName", e.target.value)} /></div>
                      <div className="form-group"><label>Phone</label><input type="text" value={formData.contactInfo.phone} onChange={(e) => updateSimple("contactInfo", "phone", e.target.value)} /></div>
                    </div>
                    <div className="form-row">
                      <div className="form-group"><label>Email</label><input type="email" value={formData.contactInfo.email} onChange={(e) => updateSimple("contactInfo", "email", e.target.value)} /></div>
                      <div className="form-group"><label>Office Hours</label><input type="text" value={formData.contactInfo.officeHours} onChange={(e) => updateSimple("contactInfo", "officeHours", e.target.value)} /></div>
                    </div>
                    <div className="form-group"><label>Address</label><textarea rows="3" value={formData.contactInfo.address} onChange={(e) => updateSimple("contactInfo", "address", e.target.value)} /></div>
                  </div>
                )}
                {key === "applyNow" && (
                  <div className="section-fields">
                    <div className="form-row">
                      <div className="form-group"><label>Button Text</label><input type="text" value={formData.applyNow.buttonText} onChange={(e) => updateSimple("applyNow", "buttonText", e.target.value)} /></div>
                      <div className="form-group"><label>Button URL</label><input type="url" value={formData.applyNow.buttonUrl} onChange={(e) => updateSimple("applyNow", "buttonUrl", e.target.value)} /></div>
                    </div>
                  </div>
                )}
                {key === "faqs" && (
                  <div className="section-fields">
                    <div className="list-items">
                      {formData.faqs.map((faq, index) => (
                        <div className="list-item-card" key={faq.id}>
                          <div className="list-item-header"><span className="item-index">FAQ {index + 1}</span><button type="button" className="remove-item-btn" onClick={() => removeListItem("faqs", faq.id)}>Remove</button></div>
                          <div className="form-group"><label>Question</label><input type="text" value={faq.question} onChange={(e) => updateListItem("faqs", faq.id, "question", e.target.value)} /></div>
                          <div className="form-group"><label>Answer</label><textarea rows="3" value={faq.answer} onChange={(e) => updateListItem("faqs", faq.id, "answer", e.target.value)} /></div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="add-item-btn" onClick={() => addListItem("faqs", { question: "", answer: "" })}><span>+</span> Add FAQ</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </form>
    </div>
  );
};

export default AdmissionCell;